// What a folder shows: the cards (or list rows) of one location of the project tree, built from the
// project data and the custom folders. Pure, so grid and list read exactly the same items.
import type { CanvasInfo } from "./canvas/types.ts";
import { type FileFacts, factsFromSummary, isMedia, kindLabel } from "./docKind.ts";
import { SUBTYPE_LABEL, SUBTYPES } from "./elements.ts";
import { type RefInfo, refKey } from "./folderRefs.ts";
import { childrenOf, type Folder, type FolderItemRef } from "./folders.ts";
import { formatSeconds, isActive, isFilm, num, spentText } from "./lib.ts";
import { generationsOf } from "./looseShot.ts";
import type { Loc, Route } from "./route.ts";
import { type EpisodeView, episodeShots, isNamedScene, namedScenes, sceneGroups } from "./scenes.ts";
import { inCutOrder } from "./structure.ts";
import type { Element, ElementSubtype, Episode, Generation, Scene, Shot, TakesResponse } from "./types.ts";
import type { ProjectData } from "./useProject.ts";

export type ItemKind = "project" | "folder" | "episode" | "scene" | "shot" | "element" | "canvas" | "asset";

/** Where a shot stands, in film words. */
export type ShotState = "picked" | "generating" | "to-pick" | "empty" | "failed";

export const SHOT_STATE_LABEL: Record<ShotState, string> = {
  picked: "Picked",
  generating: "Rendering",
  "to-pick": "Takes to pick",
  empty: "No takes",
  failed: "Failed",
};

export interface Item {
  /** `${kind}:${id}` — unique in a view */
  key: string;
  kind: ItemKind;
  id: string;
  name: string;
  /** mono label shown before the name (shot or scene code) */
  code?: string;
  /** second line on cards, "Kind" column in lists */
  detail: string;
  /** small facts for the list columns: duration, count, cost */
  facts: string[];
  /** picture: an asset version id; for elements, `element:<id>` (resolved lazily) */
  thumb: string | null;
  /** a contact strip of up to three stills (scenes, episodes and projects in a tall format) */
  strip?: string[];
  /**
   * per still of the strip, the icon it shows when it has no picture (absent: `glyph`). An uploaded file in a
   * folder's cover may be a document: it shows as a file, not as one more empty folder.
   */
  stripGlyphs?: Glyph[];
  /** which icon a card without a picture shows */
  glyph: Glyph;
  state?: ShotState;
  /** where Enter / double-click goes */
  open: Route;
  /** the item as a folder reference (absent for system folders) */
  ref?: FolderItemRef;
  /** a custom folder: can be renamed and receives drops */
  folderId?: string;
  /** the item no longer exists (archived) but a folder still points at it */
  missing?: boolean;
  /** the server is still being asked about this folder reference */
  loading?: boolean;
  /** a shot that exists but sits in no episode yet: it shows in folders, and cannot be opened from them */
  unplaced?: boolean;
  /** the shape of this card's still (width ÷ height) when it differs from the view's (project cards) */
  ar?: number;
  /** a quiet second line on a card ("Active 15 min ago") */
  note?: string;
  /** the full description (shots), for the inspector of an item the project data does not hold */
  description?: string;
  /** size and length of a shot, for the same inspector */
  size?: string | null;
  durationS?: string;
  /** an uploaded file (an asset version): its name, kind and mime, so Open shows the file itself */
  file?: FileFacts & { assetVersionId: string };
}

export type Glyph =
  | "folder"
  | "episode"
  | "scene"
  | "shot"
  | "character"
  | "location"
  | "prop"
  | "style"
  | "canvas"
  | "asset"
  | "elements";

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ─── shots ───────────────────────────────────────────────────────────────────

export interface ShotFacts {
  state: ShotState;
  /** the picked keyframe, else the newest ready keyframe take with media */
  thumb: string | null;
  takes: number;
  costUsd: number;
}

export function shotFacts(takes: TakesResponse | undefined, gens: readonly Generation[]): ShotFacts {
  const items = (takes?.items ?? []).filter((t) => t.state !== "archived");
  const ids = new Set(items.map((t) => t.id));
  const mine = gens.filter((g) => ids.has(g.take_id));
  const pick = takes?.current_selection.find((s) => s.kind === "keyframe" && s.asset_version_id);
  const anyPick = takes?.current_selection.some((s) => s.take_id);
  let cost = 0;
  for (const g of mine) if (g.status === "succeeded") cost += num(g.cost_amount) ?? 0;
  const newestKeyframe = mine
    .filter((g) => g.status === "succeeded" && g.asset_version_id && (g.kind ?? "keyframe") === "keyframe")
    .sort((a, b) => b.queued_at.localeCompare(a.queued_at))[0];
  const state: ShotState = mine.some(isActive)
    ? "generating"
    : anyPick
      ? "picked"
      : items.some((t) => t.state === "ready")
        ? "to-pick"
        : items.some((t) => t.state === "failed")
          ? "failed"
          : "empty";
  return {
    state,
    thumb: pick?.asset_version_id ?? newestKeyframe?.asset_version_id ?? null,
    takes: items.length,
    costUsd: cost,
  };
}

function episodeGens(d: ProjectData, shotId: string): Generation[] {
  return generationsOf(d, shotId);
}

const SIZE_NAME: Record<string, string> = {
  ELS: "Extreme long shot",
  EWS: "Extreme wide shot",
  LS: "Long shot",
  WS: "Wide shot",
  MLS: "Medium long shot",
  MS: "Medium shot",
  MCU: "Medium close-up",
  CU: "Close-up",
  ECU: "Extreme close-up",
  OTS: "Over the shoulder",
  POV: "Point of view",
  INS: "Insert",
};

/** "MCU" → "Medium close-up": the shot size in words, for shots without a description. */
export const sizeName = (size: string | null | undefined): string =>
  (size && SIZE_NAME[size.toUpperCase()]) || (size ? `${size} shot` : "Untitled shot");

const nameCache = new WeakMap<ProjectData, Map<string, string>>();
/** handle → element name, so "@maya-tan" reads "Maya Tan". */
export function handleNames(d: ProjectData): Map<string, string> {
  let m = nameCache.get(d);
  if (!m) {
    m = new Map(d.elements.map((e) => [e.handle, e.name]));
    nameCache.set(d, m);
  }
  return m;
}

export function shotItem(d: ProjectData, shot: Shot): Item {
  const f = shotFacts(d.takes.get(shot.id), episodeGens(d, shot.id));
  const bits = [shot.size, shot.angle, shot.movement].filter(Boolean).join(" · ");
  return {
    key: `shot:${shot.id}`,
    kind: "shot",
    id: shot.id,
    code: shot.code,
    name: firstWords(shot.description, 48, handleNames(d)) || sizeName(shot.size),
    detail: bits,
    size: shot.size,
    facts: [formatSeconds(shot.duration_s), plural(f.takes, "take"), spentText(f.costUsd)],
    thumb: f.thumb,
    glyph: "shot",
    state: f.state,
    open: { view: "shot", projectId: d.project.id, shotId: shot.id },
    ref: { kind: "shot", id: shot.id },
  };
}

/**
 * A description reads better than a code as a title: its first clause (up to a comma, full stop or
 * semicolon), @handles turned into names. Prompt text such as "wide rooftop at night, amber over blue,
 * 9:16" becomes "Wide rooftop at night". A clause too short to say anything takes the next one with it.
 */
export function firstWords(
  s: string | null | undefined,
  max = 48,
  names: ReadonlyMap<string, string> = new Map(),
): string {
  if (!s) return "";
  const clauses = s
    .replace(/@([a-z0-9-]+)/g, (_, h: string) => names.get(h) ?? h.replace(/-/g, " "))
    .split(/[;,\n]|\.(?=\s|$)/)
    .map((c) => c.trim())
    .filter(Boolean);
  let t = clauses[0] ?? "";
  for (let i = 1; t.length < 8 && i < clauses.length; i++) t = `${t}, ${clauses[i]}`;
  t = t.replace(/\s*\d+:\d+\s*$/, "").trim();
  const c = t.charAt(0).toUpperCase() + t.slice(1);
  return c.length > max ? `${c.slice(0, max - 1).trimEnd()}…` : c;
}

// ─── scenes and episodes ─────────────────────────────────────────────────────

/** The first stills of a scene in shot order (picked keyframes first come first). */
export function sceneCovers(d: ProjectData, sceneId: string, n = 3): string[] {
  const out: string[] = [];
  for (const s of d.shots.get(sceneId) ?? []) {
    const f = shotFacts(d.takes.get(s.id), episodeGens(d, s.id));
    if (f.thumb) out.push(f.thumb);
    if (out.length >= n) break;
  }
  return out;
}

export const sceneCover = (d: ProjectData, sceneId: string): string | null =>
  sceneCovers(d, sceneId, 1)[0] ?? null;

export function sceneItem(d: ProjectData, episodeId: string, scene: Scene): Item {
  const shots = d.shots.get(scene.id) ?? [];
  const facts = shots.map((s) => shotFacts(d.takes.get(s.id), episodeGens(d, s.id)));
  const picked = facts.filter((f) => f.state === "picked").length;
  const dur = shots.reduce((t, s) => t + (num(s.duration_s) ?? 0), 0);
  return {
    key: `scene:${scene.id}`,
    kind: "scene",
    id: scene.id,
    code: scene.code,
    name: scene.slugline ?? `Scene ${scene.code}`,
    detail: `${plural(shots.length, "shot")} · ${picked}/${shots.length} picked`,
    facts: [formatSeconds(dur), plural(shots.length, "shot"), `${picked} picked`],
    thumb: sceneCover(d, scene.id),
    strip: sceneCovers(d, scene.id),
    glyph: "scene",
    state: facts.some((f) => f.state === "generating") ? "generating" : undefined,
    open: { view: "project", projectId: d.project.id, loc: { kind: "scene", episodeId, id: scene.id } },
    ref: { kind: "scene", id: scene.id },
  };
}

export const episodeName = (e: Episode) => e.title ?? `Episode ${e.number}`;
export const episodeCode = (e: Episode) => e.code ?? `E${String(e.number).padStart(2, "0")}`;

export function episodeItem(d: ProjectData, e: Episode): Item {
  const scenes = d.scenes.get(e.id) ?? [];
  const named = namedScenes(d, e.id);
  const shots = episodeShots(d, e.id);
  const strip = scenes.flatMap((s) => sceneCovers(d, s.id)).slice(0, 3);
  const cost = num(d.costs.get(e.id)?.total_usd);
  const detail = [plural(shots.length, "shot"), named.length ? plural(named.length, "scene") : ""]
    .filter(Boolean)
    .join(" · ");
  return {
    key: `episode:${e.id}`,
    kind: "episode",
    id: e.id,
    code: episodeCode(e),
    name: episodeName(e),
    detail,
    facts: [e.status, plural(shots.length, "shot"), spentText(cost)],
    thumb: strip[0] ?? null,
    strip,
    glyph: "episode",
    open: { view: "project", projectId: d.project.id, loc: { kind: "episode", id: e.id } },
    ref: { kind: "episode", id: e.id },
  };
}

// ─── elements, canvases, assets ──────────────────────────────────────────────

export function elementItem(d: ProjectData, el: Element): Item {
  const sub = (SUBTYPES as readonly string[]).includes(el.subtype) ? (el.subtype as ElementSubtype) : "prop";
  return {
    key: `element:${el.id}`,
    kind: "element",
    id: el.id,
    name: el.name,
    detail: `@${el.handle}`,
    facts: [SUBTYPE_LABEL[sub].replace(/s$/, ""), `v${el.current_version ?? 1}`, ""],
    thumb: `element:${el.id}:${el.current_version ?? 1}`,
    glyph: sub,
    open: { view: "element", projectId: d.project.id, elementId: el.id },
    ref: { kind: "element", id: el.id },
  };
}

const SCOPE_LABEL: Record<string, string> = {
  shot: "Shot board",
  scene: "Scene board",
  episode: "Episode board",
  element: "Element board",
  free: "Free board",
};

export function canvasItem(d: ProjectData, c: CanvasInfo): Item {
  const shot = c.scope === "shot" && c.scopeId ? d.shotById.get(c.scopeId) : undefined;
  return {
    key: `canvas:${c.id}`,
    kind: "canvas",
    id: c.id,
    name: c.name ?? "Untitled canvas",
    detail: shot ? `${SCOPE_LABEL.shot} · ${shot.code}` : (SCOPE_LABEL[c.scope] ?? "Board"),
    facts: [SCOPE_LABEL[c.scope] ?? "Board", "", ""],
    thumb: shot ? shotFacts(d.takes.get(shot.id), episodeGens(d, shot.id)).thumb : null,
    glyph: "canvas",
    open: { view: "canvas", projectId: d.project.id, id: c.id },
    ref: { kind: "canvas", id: c.id },
  };
}

export interface AssetRow {
  assetVersionId: string;
  name: string;
  detail: string;
  /** what the server said about the file (folder references); absent for imported takes */
  file?: FileFacts;
}

/** A file the server lists for the production (GET /projects/{id}/files), in a folder or not. */
export interface ProjectFile {
  asset_version_id: string;
  name: string | null;
  kind: string;
  mime: string;
}

/**
 * Every file of the production: the takes imported onto its shots, the files in its folders (a Higgsfield import
 * puts its takes and stills there, not on shots), then what the server lists besides, so a file that left its folder
 * is still here. Each file once, by its own name when the server gave it.
 */
export function productionAssets(
  d: ProjectData,
  folders: readonly Folder[],
  files: readonly ProjectFile[] = [],
): AssetRow[] {
  const out = importedAssets(d);
  const seen = new Set(out.map((a) => a.assetVersionId));
  for (const f of folders)
    for (const r of f.items) {
      if (r.kind !== "asset" || seen.has(r.id)) continue;
      seen.add(r.id);
      out.push({ assetVersionId: r.id, name: "File", detail: f.name });
    }
  const byId = new Map(out.map((a) => [a.assetVersionId, a]));
  for (const x of files) {
    const facts: FileFacts = { name: x.name, assetKind: x.kind, mime: x.mime };
    const row = byId.get(x.asset_version_id);
    if (row) {
      row.name = x.name ?? row.name;
      row.file = facts;
    } else
      out.push({
        assetVersionId: x.asset_version_id,
        name: x.name ?? "File",
        detail: kindLabel(facts),
        file: facts,
      });
  }
  return out;
}

export function importedAssets(d: ProjectData): AssetRow[] {
  const out: AssetRow[] = [];
  const seen = new Set<string>();
  for (const [shotId, r] of d.takes) {
    const shot = d.shotById.get(shotId);
    for (const t of r.items) {
      if (t.origin_kind !== "import") continue;
      const sel = r.current_selection.find((s) => s.take_id === t.id);
      const av = t.asset_version_id ?? sel?.asset_version_id ?? null;
      if (!av || seen.has(av)) continue;
      seen.add(av);
      out.push({
        assetVersionId: av,
        name: t.label ?? "Imported file",
        detail: `${t.kind} · ${shot?.code ?? ""}`,
      });
    }
  }
  return out;
}

export function assetItem(d: ProjectData, a: AssetRow): Item {
  const f = a.file;
  // a document has no still: the card shows the file glyph instead of a picture that cannot load
  const still = !f || isMedia(f);
  return {
    key: `asset:${a.assetVersionId}`,
    kind: "asset",
    id: a.assetVersionId,
    name: a.name,
    detail: a.detail,
    facts: f ? [a.detail, "", ""] : ["Upload", a.detail, ""],
    thumb: still ? a.assetVersionId : null,
    glyph: "asset",
    // Open shows the file itself (ProjectView opens the file view for an item with `file`)
    file: { assetVersionId: a.assetVersionId, ...(f ?? { name: a.name, assetKind: null, mime: null }) },
    open: { view: "project", projectId: d.project.id, loc: { kind: "assets" } },
    ref: { kind: "asset", id: a.assetVersionId },
  };
}

// ─── folders ─────────────────────────────────────────────────────────────────

function systemFolder(
  d: ProjectData,
  loc: Loc,
  name: string,
  detail: string,
  glyph: Glyph,
  stills: readonly string[] = [],
): Item {
  return {
    key: `sys:${loc.kind}${"subtype" in loc && loc.subtype ? `:${loc.subtype}` : ""}`,
    kind: "folder",
    id: loc.kind,
    name,
    detail,
    facts: ["Folder", detail, ""],
    thumb: stills[0] ?? null,
    strip: stills.length > 1 ? [...stills] : undefined,
    glyph,
    open: { view: "project", projectId: d.project.id, loc },
  };
}

/** Up to `n` different stills, in order, from a list of candidate pictures (empty ones are skipped). */
function firstStills(candidates: Iterable<string | null | undefined>, n = 3): string[] {
  const out: string[] = [];
  for (const c of candidates) {
    if (!c || c.startsWith("element:") || out.includes(c)) continue;
    out.push(c);
    if (out.length >= n) break;
  }
  return out;
}

/**
 * A custom folder's cover: its stills, and the icon each shows without a picture. Outside the open folder the
 * server has not said which uploaded files are documents, so a file counts as a still (it may be a picture) and
 * shows the file icon when it has none.
 */
export function folderCover(
  d: ProjectData,
  f: Folder,
  folders: readonly Folder[],
  o: ItemsOptions = {},
  n = 3,
): { stills: string[]; glyphs: Glyph[] } {
  const found: string[] = [];
  const files = new Set<string>();
  const seen = new Set<string>();
  const walk = (folder: Folder, depth: number) => {
    if (found.length >= n || seen.has(folder.id) || depth > 8) return;
    seen.add(folder.id);
    for (const r of folder.items) {
      const it = resolveRef(d, r, o.refInfos, false);
      if (it.missing || !it.thumb) continue;
      found.push(it.thumb);
      if (it.kind === "asset") files.add(it.thumb);
    }
    for (const c of childrenOf(folders, folder.id)) walk(c, depth + 1);
  };
  walk(f, 0);
  const stills = firstStills(found, n);
  return { stills, glyphs: stills.map((t) => (files.has(t) ? "asset" : "folder")) };
}

export function customFolderItem(
  d: ProjectData,
  f: Folder,
  folders: readonly Folder[],
  o: ItemsOptions = {},
): Item {
  const { stills, glyphs } = folderCover(d, f, folders, o);
  const subs = childrenOf(folders, f.id).length;
  const parts = [
    subs ? plural(subs, "folder") : "",
    f.items.length ? plural(f.items.length, "item") : "",
  ].filter(Boolean);
  return {
    key: `folder:${f.id}`,
    kind: "folder",
    id: f.id,
    name: f.name,
    detail: parts.join(" · ") || "Empty",
    facts: ["Folder", parts.join(" · ") || "Empty", ""],
    thumb: stills[0] ?? null,
    strip: stills.length > 1 ? stills : undefined,
    stripGlyphs: stills.length > 1 ? glyphs : undefined,
    glyph: "folder",
    open: { view: "project", projectId: d.project.id, loc: { kind: "folder", id: f.id } },
    folderId: f.id,
  };
}

/** What the server knows about the folder references the project data does not hold, by `kind:id`. */
export type RefInfos = ReadonlyMap<string, RefInfo>;

const GLYPH_OF: Record<string, Glyph> = { canvas: "canvas", element: "elements", asset: "asset" };

/** The still of a shot the project data does not hold: its picked keyframe, else the newest ready one. */
export function unplacedThumb(takes: TakesResponse | null | undefined): string | null {
  const picked = takes?.current_selection.find((s) => s.kind === "keyframe" && s.asset_version_id);
  if (picked?.asset_version_id) return picked.asset_version_id;
  const ready = (takes?.items ?? [])
    .filter((t) => t.kind === "keyframe" && t.state === "ready" && t.asset_version_id)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  return ready?.asset_version_id ?? null;
}

const text = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);

/** A shot that exists but sits in no scene: shown like any other shot (code, title, still), read from the server's summary. */
function unplacedShotItem(d: ProjectData, ref: FolderItemRef, info: RefInfo): Item {
  const sum = info.summary ?? {};
  const description = text(sum.description);
  const size = text(sum.size) ?? null;
  const f = shotFacts(info.takes ?? undefined, []);
  const duration =
    text(sum.duration_s) ?? (typeof sum.duration_s === "number" ? String(sum.duration_s) : undefined);
  return {
    key: `shot:${ref.id}`,
    kind: "shot",
    id: ref.id,
    code: text(sum.code),
    name: firstWords(description, 48, handleNames(d)) || sizeName(size),
    detail: size ?? "",
    facts: [formatSeconds(duration), plural(f.takes, "take"), spentText(f.costUsd)],
    thumb: unplacedThumb(info.takes) ?? f.thumb,
    glyph: "shot",
    state: f.state,
    // Opens on its own: the shot view loads a shot that sits in no scene by its id
    open: { view: "shot", projectId: d.project.id, shotId: ref.id },
    ref,
    unplaced: true,
    description,
    size,
    durationS: duration,
  };
}

/** An uploaded file in a folder: its real name, a short kind (Script, PDF, Word file …) from the folder endpoint. */
export function fileItem(d: ProjectData, assetVersionId: string, info: RefInfo): Item {
  const f = factsFromSummary(info.summary);
  return assetItem(d, { assetVersionId, name: f.name ?? "Untitled file", detail: kindLabel(f), file: f });
}

/** An item the server still has but the project data does not list (an element or canvas of another project). */
function foreignItem(d: ProjectData, ref: FolderItemRef, info: RefInfo): Item {
  const sum = info.summary ?? {};
  const name = text(sum.name) ?? text(sum.title) ?? text(sum.code) ?? "Untitled";
  return {
    key: `${ref.kind}:${ref.id}`,
    kind: ref.kind === "scene" || ref.kind === "episode" ? "folder" : ref.kind,
    id: ref.id,
    code: text(sum.code),
    name,
    detail: ref.kind === "canvas" ? "Board" : ref.kind === "element" ? "Element" : "",
    facts: ["", "", ""],
    thumb: null,
    glyph: GLYPH_OF[ref.kind] ?? "shot",
    open:
      ref.kind === "canvas"
        ? { view: "canvas", projectId: d.project.id, id: ref.id }
        : ref.kind === "element"
          ? { view: "element", projectId: d.project.id, elementId: ref.id }
          : { view: "project", projectId: d.project.id, loc: { kind: "root" } },
    ref,
  };
}

/**
 * The item a folder reference points at. The project data holds what it can; for the rest, `infos` (what the
 * folder endpoint said, see folderRefs.ts) tells an item that exists but is not in the data (a shot with no
 * placement) from one that was archived. Archived items keep their identity: code, last title, the still.
 * While the server has not answered, a quiet placeholder stands in (never "No longer available").
 */
export function resolveRef(d: ProjectData, ref: FolderItemRef, infos?: RefInfos, loading = false): Item {
  switch (ref.kind) {
    case "shot": {
      const s = d.shotById.get(ref.id);
      if (s) return shotItem(d, s);
      break;
    }
    case "canvas": {
      const c = d.canvases.find((x) => x.id === ref.id);
      if (c) return canvasItem(d, c);
      break;
    }
    case "element": {
      const e = d.elements.find((x) => x.id === ref.id);
      if (e) return elementItem(d, e);
      break;
    }
    case "scene":
      for (const [eid, list] of d.scenes) {
        const s = list.find((x) => x.id === ref.id);
        if (s) return sceneItem(d, eid, s);
      }
      break;
    case "episode": {
      const e = d.episodes.find((x) => x.id === ref.id);
      if (e) return episodeItem(d, e);
      break;
    }
    case "asset": {
      const info = infos?.get(refKey(ref));
      if (info?.available) return fileItem(d, ref.id, info);
      // the server could not be asked: still a file that opens, just without its name
      if (!info && !loading) return assetItem(d, { assetVersionId: ref.id, name: "File", detail: "Upload" });
      break;
    }
  }
  const info = infos?.get(refKey(ref));
  if (info?.available)
    return ref.kind === "shot" ? unplacedShotItem(d, ref, info) : foreignItem(d, ref, info);
  const base: Item = {
    key: `${ref.kind}:${ref.id}`,
    kind: ref.kind === "scene" || ref.kind === "episode" ? "folder" : ref.kind,
    id: ref.id,
    name: "",
    detail: "",
    facts: ["", "", ""],
    thumb: null,
    glyph: GLYPH_OF[ref.kind] ?? "shot",
    open: { view: "project", projectId: d.project.id, loc: { kind: "root" } },
    ref,
  };
  if (loading && !info) return { ...base, name: "Loading…", loading: true };
  const old = info?.archived;
  // No answer from the server at all (it failed): say so, never claim the item was archived.
  const gone = info ? "Archived" : "Unavailable";
  return {
    ...base,
    code: old?.code,
    name:
      (ref.kind === "shot" ? firstWords(old?.name, 48, handleNames(d)) : old?.name) || `${gone} ${ref.kind}`,
    detail: gone,
    thumb: info ? unplacedThumb(info.takes) : null,
    missing: true,
  };
}

export interface ItemsOptions {
  /** what the server said about the folder references the project data does not hold */
  refInfos?: RefInfos;
  /** the server has not answered about them yet */
  refsLoading?: boolean;
  /** how an episode folder lays out its shots (By scene / All shots) */
  episodeView?: EpisodeView;
  /** cut order of the episode's shots (shot id -> position), from the episode structure */
  order?: ReadonlyMap<string, number>;
  /** the production's files as the server lists them */
  files?: readonly ProjectFile[];
}

/** A labelled stretch of the cards: a scene (or "No scene") inside an episode shown By scene. */
export interface Section {
  key: string;
  title: string;
  code?: string;
  detail: string;
  /** opens the scene (absent for "No scene") */
  open?: Route;
  /** keys of the items in this section, in order */
  itemKeys: string[];
}

/** The groups an episode folder shows in By scene mode; null when the cards are one flat list. */
export function sectionsAt(d: ProjectData, loc: Loc, view: EpisodeView): Section[] | null {
  if (loc.kind !== "episode" || view !== "scenes") return null;
  const groups = sceneGroups(d, loc.id);
  if (groups.length === 1 && !groups[0]!.scene) return null;
  return groups.map((g) => ({
    key: g.key,
    title: g.scene ? (g.scene.slugline ?? `Scene ${g.scene.code}`) : "No scene",
    code: g.scene?.code,
    detail: plural(g.shots.length, "shot"),
    open: g.scene
      ? {
          view: "project",
          projectId: d.project.id,
          loc: { kind: "scene", episodeId: loc.id, id: g.scene.id },
        }
      : undefined,
    itemKeys: g.shots.map((s) => `shot:${s.id}`),
  }));
}

/** The cards of one folder. */
export function itemsAt(d: ProjectData, loc: Loc, folders: readonly Folder[], o: ItemsOptions = {}): Item[] {
  switch (loc.kind) {
    case "root": {
      const shots = d.shotById.size;
      const film = filmEpisode(d);
      return [
        film !== undefined
          ? film
            ? { ...episodeItem(d, film), name: "Film", code: undefined }
            : systemFolder(d, { kind: "episodes" }, "Film", "No shots yet", "episode")
          : systemFolder(
              d,
              { kind: "episodes" },
              "Episodes",
              `${plural(d.episodes.length, "episode")} · ${plural(shots, "shot")}`,
              "episode",
              firstStills(d.episodes.map((e) => episodeItem(d, e).thumb)),
            ),
        systemFolder(d, { kind: "elements" }, "Elements", plural(d.elements.length, "element"), "elements"),
        systemFolder(
          d,
          { kind: "canvases" },
          "Canvases",
          plural(d.canvases.length, "canvas", "canvases"),
          "canvas",
          firstStills(d.canvases.map((c) => canvasItem(d, c).thumb)),
        ),
        systemFolder(
          d,
          { kind: "assets" },
          "Assets",
          "Uploads and imports",
          "asset",
          firstStills(
            productionAssets(d, folders, o.files)
              .filter((a) => !a.file || isMedia(a.file))
              .map((a) => a.assetVersionId),
          ),
        ),
        ...childrenOf(folders, null).map((f) => customFolderItem(d, f, folders, o)),
      ];
    }
    case "episodes":
      return d.episodes.map((e) => episodeItem(d, e));
    case "episode": {
      // All shots: one flat list in cut order. By scene: the same shots, grouped by named scene (see sectionsAt).
      const view = o.episodeView ?? "all";
      const shots =
        view === "scenes"
          ? sceneGroups(d, loc.id).flatMap((g) => g.shots)
          : inCutOrder(episodeShots(d, loc.id), o.order);
      return shots.map((s) => shotItem(d, s));
    }
    case "scene":
      return (d.shots.get(loc.id) ?? []).map((s) => shotItem(d, s));
    case "elements":
      if (!loc.subtype)
        return SUBTYPES.map((st) =>
          systemFolder(
            d,
            { kind: "elements", subtype: st },
            SUBTYPE_LABEL[st],
            plural(d.elements.filter((e) => e.subtype === st).length, "element"),
            st,
          ),
        );
      return d.elements
        .filter((e) => e.subtype === loc.subtype)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => elementItem(d, e));
    case "canvases":
      return d.canvases.map((c) => canvasItem(d, c));
    case "assets":
      return productionAssets(d, folders, o.files).map((a) => assetItem(d, a));
    case "makers":
    case "crew":
    case "settings":
    case "cut":
    case "storyboard":
    case "tools":
    case "review":
    case "blocking":
    case "tags":
      // Makers, Crew, Settings, the Cut and the other studio tabs have their own panes; they are not grids of cards.
      return [];
    case "folder": {
      const f = folders.find((x) => x.id === loc.id);
      if (!f) return [];
      return [
        ...childrenOf(folders, f.id).map((c) => customFolderItem(d, c, folders, o)),
        ...f.items.map((r) => resolveRef(d, r, o.refInfos, o.refsLoading)),
      ];
    }
  }
}

// ─── breadcrumbs ─────────────────────────────────────────────────────────────

export interface Crumb {
  label: string;
  route: Route;
}

export function crumbsFor(d: ProjectData, loc: Loc, folders: readonly Folder[]): Crumb[] {
  const p = d.project.id;
  const at = (l: Loc): Route => ({ view: "project", projectId: p, loc: l });
  const out: Crumb[] = [{ label: d.project.name, route: at({ kind: "root" }) }];
  switch (loc.kind) {
    case "root":
      break;
    case "episodes":
      out.push({ label: filmEpisode(d) !== undefined ? "Film" : "Episodes", route: at(loc) });
      break;
    case "episode":
    case "scene":
    case "cut":
    case "storyboard":
    case "tools":
    case "review":
    case "blocking":
    case "tags": {
      const eid = loc.kind === "episode" ? loc.id : loc.episodeId;
      const e = d.episodes.find((x) => x.id === eid);
      if (filmEpisode(d)) out.push({ label: "Film", route: at({ kind: "episode", id: eid }) });
      else {
        out.push({ label: "Episodes", route: at({ kind: "episodes" }) });
        out.push({
          label: e ? `${episodeCode(e)} ${episodeName(e)}` : "Episode",
          route: at({ kind: "episode", id: eid }),
        });
      }
      if (loc.kind === "scene") {
        const s = (d.scenes.get(eid) ?? []).find((x) => x.id === loc.id);
        out.push({ label: s ? `${s.code} ${s.slugline ?? ""}`.trim() : "Scene", route: at(loc) });
      }
      if (loc.kind === "cut") out.push({ label: "Cut", route: at(loc) });
      if (loc.kind === "storyboard") out.push({ label: "Storyboard", route: at(loc) });
      if (loc.kind === "tools") out.push({ label: "Tools", route: at(loc) });
      if (loc.kind === "review") out.push({ label: "Edit", route: at(loc) });
      if (loc.kind === "blocking") out.push({ label: "3D", route: at(loc) });
      if (loc.kind === "tags") out.push({ label: "Script", route: at(loc) });
      break;
    }
    case "elements":
      out.push({ label: "Elements", route: at({ kind: "elements" }) });
      if (loc.subtype) out.push({ label: SUBTYPE_LABEL[loc.subtype], route: at(loc) });
      break;
    case "canvases":
      out.push({ label: "Canvases", route: at(loc) });
      break;
    case "assets":
      out.push({ label: "Assets", route: at(loc) });
      break;
    case "settings":
      out.push({ label: "Settings", route: at(loc) });
      break;
    case "makers":
      out.push({ label: "Makers", route: at({ kind: "makers" }) });
      if (loc.page?.t === "brief")
        out.push({ label: loc.page.id === "new" ? "New brief" : "Brief", route: at(loc) });
      if (loc.page?.t === "assignment") out.push({ label: "Assignment", route: at(loc) });
      break;
    case "folder": {
      const byId = new Map(folders.map((f) => [f.id, f]));
      const chain: Folder[] = [];
      let cur = byId.get(loc.id);
      const seen = new Set<string>();
      while (cur && !seen.has(cur.id)) {
        seen.add(cur.id);
        chain.unshift(cur);
        cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      }
      for (const f of chain) out.push({ label: f.name, route: at({ kind: "folder", id: f.id }) });
      break;
    }
  }
  return out;
}

/** The location a shot lives in (its scene folder). */
export function shotLoc(d: ProjectData, shotId: string): Loc {
  const h = d.shotHome.get(shotId);
  if (!h) return { kind: "episodes" };
  // Unnamed scenes are not places you can be in: a shot there lives in its episode.
  const scene = (d.scenes.get(h.episodeId) ?? []).find((s) => s.id === h.sceneId);
  return scene && isNamedScene(scene)
    ? { kind: "scene", episodeId: h.episodeId, id: h.sceneId }
    : { kind: "episode", id: h.episodeId };
}

export function elementLoc(d: ProjectData, elementId: string): Loc {
  const e = d.elements.find((x) => x.id === elementId);
  const st =
    e && (SUBTYPES as readonly string[]).includes(e.subtype) ? (e.subtype as ElementSubtype) : undefined;
  return st ? { kind: "elements", subtype: st } : { kind: "elements" };
}

/**
 * The one episode of a film (a feature or a short): `null` while it has none yet, `undefined` when the
 * production is not a film (or a film that somehow has several episodes, which then shows them all).
 */
export function filmEpisode(d: Pick<ProjectData, "project" | "episodes">): Episode | null | undefined {
  if (!isFilm(d.project.type) || d.episodes.length > 1) return undefined;
  return d.episodes[0] ?? null;
}

/** Parent of a location, for Backspace / ⌘↑. A film's episode sits right under the production. */
export function parentLoc(loc: Loc, folders: readonly Folder[], film = false): Loc | null {
  switch (loc.kind) {
    case "root":
      return null;
    case "episode":
      return film ? { kind: "root" } : { kind: "episodes" };
    case "scene":
    case "cut":
    case "storyboard":
    case "tools":
    case "review":
    case "blocking":
    case "tags":
      return { kind: "episode", id: loc.episodeId };
    case "elements":
      return loc.subtype ? { kind: "elements" } : { kind: "root" };
    case "folder": {
      const f = folders.find((x) => x.id === loc.id);
      return f?.parentId ? { kind: "folder", id: f.parentId } : { kind: "root" };
    }
    case "makers":
      return loc.page ? { kind: "makers" } : { kind: "root" };
    default:
      return { kind: "root" };
  }
}

/** The opened container itself, so narrow screens can review it without selecting a child card. */
export function openedContainerItem(d: ProjectData, loc: Loc, folders: readonly Folder[]): Item | null {
  if (loc.kind === "episode") {
    const episode = d.episodes.find((e) => e.id === loc.id);
    return episode ? episodeItem(d, episode) : null;
  }
  if (loc.kind === "folder") {
    const folder = folders.find((f) => f.id === loc.id && !f.archivedAt);
    return folder ? customFolderItem(d, folder, folders) : null;
  }
  return null;
}
